import { dispatchAppEvent, addEventListener } from './event-broadcast';
import EventBus from './event-bus';
import { localStorageAvaiable, microSeconds, checkIsIOS, getIOSVersion, compareVersion } from './utils';
import { Singleflight } from './singleflight';
const SharedWorkerSupported = 'SharedWorker' in globalThis;
const EVENT_TO_MAIN_WORKER = 'toMainWorker'; // 发送给主 worker 的消息
const EVENT_TO_VIRTUAL_WORKER = 'toVirtualWorker'; // 发送给虚拟 worker 的消息
function generateUUID() {
    function S4() {
        return ((1 + Math.random()) * 0x10000 || 0).toString(16).substring(1);
    }
    return `${S4()}-${S4()}`;
}
var WorkerType;
(function (WorkerType) {
    WorkerType[WorkerType["Main"] = 1] = "Main";
    WorkerType[WorkerType["Virtual"] = 2] = "Virtual";
})(WorkerType || (WorkerType = {}));
const EVENT_ACT_PREFIX = 'act_'; // 确认消息的前缀
const EVENT_POST_MESSAGE = 'postMessage'; // 发送消息
const EVENT_ADD_EVENT_LISTENER = 'addEventListener'; // 绑定事件
const EVENT_TOGGLE_MAIN_WORKER = 'toggleMainWorker'; // 切换为主 worker
const EVENT_MAIN_WORKER_CREATED = 'mainWorkerCreated'; // 主 worker 创建成功
const EVENT_ADD_VIRTUAL_WORKER = 'addVirtualWorker'; // 新增虚拟 worker
const EVENT_REMOVE_VIRTUAL_WORKER = 'removeVirtualWorker'; // 移除虚拟 worker
const EVENT_RESTART = 'restart'; // 重启
const EVENT_UNLOAD = 'unload'; // 页面卸载
/**
 * SharedWorker 模式
 */
export var SharedWorkerMode;
(function (SharedWorkerMode) {
    /**
     * 自动模式，在支持 SharedWorker 的环境使用原生的，不支持的环境使用 WebWorker 模拟
     */
    SharedWorkerMode[SharedWorkerMode["Auto"] = 1] = "Auto";
    /**
     * 使用 WebWorker 模拟，主要用于调试
     */
    SharedWorkerMode[SharedWorkerMode["Simulate"] = 2] = "Simulate";
})(SharedWorkerMode || (SharedWorkerMode = {}));
const MAIN_WORKER_CACHE_KEY_PREFIX = 'mainWorkerId_';
const MAIN_WORKER_REPORT_TIME_CACHE_KEY_PREFIX = 'mainWorkerReportTime_';
const VIRTUAL_WORKER_REPORT_TIME_CACHE_KEY_PREFIX = 'virtualWorkerReportTime_';
const DEGRADE_SIMULATE_WORKER_CACHE_KEY = 'degradeSimulateWorker';
const WORKER_REPORT_INTERVAL = 5000;
let storageAvaiable;
const getStorageAvaiable = () => {
    if (typeof storageAvaiable === 'undefined') {
        storageAvaiable = localStorageAvaiable();
    }
    return storageAvaiable;
};
let globalInit = false;
/**
 * shared worker polyfill，在不支持原生 SharedWorker 的场景（iOS < 16、android）基于消息通信 + worker 模拟
 */
export class SharedWorkerPolyfill {
    constructor(url, opts) {
        this.workerType = WorkerType.Virtual;
        this.debug = false;
        this.group = '';
        this.mode = SharedWorkerMode.Auto;
        this.inited = false;
        this.callbackMaps = new Map();
        this.callbackTimers = new Map();
        this.uniqueId = 0;
        this.onMessageHandler = null;
        this.onMessageErrorHandler = null;
        this.onErrorHandler = null;
        this.messageQueue = [];
        this.eventOnMap = new Map();
        this.eventListenerMap = new Map();
        this.eventListenerQueue = new Map();
        this.virtualWorkerIds = [];
        this.latestMainWorkerId = '';
        this.closed = false;
        this.restartLock = false;
        this.onRestartHandler = null;
        this.degradeSimulateWorker = false;
        this.pageVisibleTime = 0;
        this.workerId = generateUUID();
        this.debugInfo(`new worker id: ${this.workerId}`);
        if (opts && 'mode' in opts) {
            this.mode = opts.mode;
        }
        this.url = url;
        this.opts = opts;
        this.group = opts.name || url.toString();
        this.debug = !!opts.debug;
        if (getStorageAvaiable()) {
            let degradeSimulateWorker = !!localStorage.getItem(DEGRADE_SIMULATE_WORKER_CACHE_KEY);
            if (degradeSimulateWorker) {
                /**
                 * 如果没有模拟的 worker 存在，恢复为原生 SharedWorker 模式
                 * 因为原生 SharedWorker 性能更好，虽然可能卡死
                 */
                if (!this.mainWorkerAlive && !this.virtualWorkerAlive) {
                    degradeSimulateWorker = false;
                    localStorage.removeItem(DEGRADE_SIMULATE_WORKER_CACHE_KEY);
                }
            }
            this.degradeSimulateWorker = degradeSimulateWorker;
        }
        addEventListener(this.group, EVENT_RESTART, () => {
            this.degradeToVirtualWorker();
            this.innerRestart(url, opts);
        });
        EventBus.on(this.group + EVENT_RESTART, this, () => {
            this.workerType = WorkerType.Main;
            this.innerRestart(url, opts);
        });
        if (this.useNativeSharedWorker) {
            this.inited = true;
            this.ActualWorker = new SharedWorker(url, opts);
            this.start();
        }
        else {
            this.initWorker(url, opts);
        }
        this.pageVisible = document.visibilityState === 'visible';
        document.addEventListener('visibilitychange', () => {
            this.pageVisible = document.visibilityState === 'visible';
            if (this.pageVisible) {
                this.pageVisibleTime = Date.now();
            }
        });
        this.addEventListener('message', (e) => {
            if (this.heartCheckTimer) {
                clearTimeout(this.heartCheckTimer);
                this.heartCheckTimer = undefined;
            }
        });
        if (!globalInit) {
            globalInit = true;
            window.addEventListener('unload', () => {
                this.beforeUnload();
            });
        }
    }
    debugInfo(message) {
        this.debug && console.log(message);
    }
    /**
     * ios 16.1 shared worker 异常
     */
    get useNativeSharedWorker() {
        return (SharedWorkerSupported &&
            this.mode === SharedWorkerMode.Auto &&
            !this.degradeSimulateWorker &&
            (!checkIsIOS() || compareVersion(getIOSVersion(), '16.1.0') < 0 || compareVersion(getIOSVersion(), '16.2.0') >= 0));
    }
    get mainWorkerCacheKey() {
        return `${MAIN_WORKER_CACHE_KEY_PREFIX}${this.group}`;
    }
    get mainWorkerReportTimeCacheKey() {
        return `${MAIN_WORKER_REPORT_TIME_CACHE_KEY_PREFIX}${this.group}`;
    }
    get virtualWorkerReportTimeCacheKey() {
        return `${VIRTUAL_WORKER_REPORT_TIME_CACHE_KEY_PREFIX}${this.group}`;
    }
    /**
     * 主 worker 是否存活
     */
    get mainWorkerAlive() {
        if (getStorageAvaiable()) {
            const mainWorkerId = localStorage.getItem(this.mainWorkerCacheKey);
            const mainWorkerReportTime = parseInt(localStorage.getItem(this.mainWorkerReportTimeCacheKey) || '0');
            return !!mainWorkerId && Date.now() - mainWorkerReportTime < WORKER_REPORT_INTERVAL * 2;
        }
        else {
            // storage 不可用时假定主 worker 存在
            return true;
        }
    }
    /**
     * 是否有虚拟 worker 存在且存活
     */
    get virtualWorkerAlive() {
        if (getStorageAvaiable()) {
            const virtualWorkerReportTime = parseInt(localStorage.getItem(this.virtualWorkerReportTimeCacheKey) || '0');
            return Date.now() - virtualWorkerReportTime < WORKER_REPORT_INTERVAL * 2;
        }
        else {
            // storage 不可用时假定虚拟 worker 存在
            return true;
        }
    }
    /**
     * 当前激活的 worker
     */
    get mainWorkerId() {
        if (getStorageAvaiable()) {
            const mainWorkerId = localStorage.getItem(this.mainWorkerCacheKey);
            return mainWorkerId || '';
        }
        else {
            return '';
        }
    }
    /**
     * 初始化 worker
     * @param url
     * @param opts
     */
    initWorker(url, opts) {
        /**
         * 1. 开启消息监听
         */
        if (!SharedWorkerPolyfill.listeners.get(this.group)) {
            /**
             * 同一组只监听一次页面通信，再通过 EventBus 转发到实例
             */
            SharedWorkerPolyfill.listeners.set(this.group, true);
            addEventListener(this.group, EVENT_TO_VIRTUAL_WORKER, (e) => {
                EventBus.emit(this.group + EVENT_TO_VIRTUAL_WORKER, e);
            });
            addEventListener(this.group, EVENT_TO_MAIN_WORKER, (e) => {
                EventBus.emit(this.group + EVENT_TO_MAIN_WORKER, e);
            });
        }
        EventBus.on(this.group + EVENT_TO_VIRTUAL_WORKER, this, (_name, e) => {
            const detail = e.detail;
            if (this.workerType === WorkerType.Main) {
                if (detail.event === EVENT_MAIN_WORKER_CREATED &&
                    detail.data.workerId !== this.workerId &&
                    detail.data.time >= this.mainWorkerCreatedAt) {
                    // 极端情况下，如果有其他页面重复创建了主 worker，将当前页面降级为虚拟 worker，避免同时存在多个主 worker 的场景
                    if (this.degradeTimer) {
                        clearTimeout(this.degradeTimer);
                    }
                    this.degradeTimer = setTimeout(() => {
                        const mainWorkerId = this.mainWorkerId;
                        if (mainWorkerId && mainWorkerId === this.workerId) {
                            return;
                        }
                        this.debugInfo(`other page created main worker ${detail.data.workerId}，remove current`);
                        this.degradeToVirtualWorker();
                        this.degradeTimer = undefined;
                    }, 100);
                }
                return;
            }
            if (!this.closed && (!detail.workerId || detail.workerId === this.workerId)) {
                this.onReceiveMessageFromMainWorker(detail);
            }
        });
        EventBus.on(this.group + EVENT_TO_MAIN_WORKER, this, (_name, e) => {
            if (this.workerType === WorkerType.Virtual) {
                return;
            }
            const detail = e.detail;
            this.onReceiveMessageFromVirtualWorker(detail);
        });
        /**
         * 2. 检测是否开启了主 worker
         */
        if (getStorageAvaiable()) {
            if (this.mainWorkerAlive) {
                this.initVirtualWorker(url, opts, true);
            }
            else {
                this.initMainWorker(url, opts);
            }
        }
        else {
            this.initVirtualWorker(url, opts, true);
        }
        /**
         * 3. 页面即将销毁，如果是多 webView 场景，目前不会触发该事件
         */
        window.addEventListener('beforeunload', () => {
            this.beforeUnload();
        });
        /**
         * 4. 页面可见性变化
         * 页面可见时定时检测主 worker 是否存在，解决部分场景 beforeunload 不触发导致流程异常的问题
         * 页面不可见时关闭定时器
         */
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                if (this.workerType === WorkerType.Virtual) {
                    /**
                     * 移动端有转场动画，延迟检测
                     */
                    setTimeout(() => {
                        this.debugInfo('detect main worker');
                        this.tryInitMainWorker(url, opts);
                    }, 500);
                }
                this.detectMainWorkerTimer = setInterval(() => {
                    if (this.workerType === WorkerType.Virtual) {
                        this.tryInitMainWorker(url, opts);
                    }
                }, WORKER_REPORT_INTERVAL);
            }
            else {
                if (this.detectMainWorkerTimer) {
                    clearInterval(this.detectMainWorkerTimer);
                    this.detectMainWorkerTimer = undefined;
                }
            }
        });
        this.detectMainWorkerTimer = setInterval(() => {
            if (this.workerType === WorkerType.Virtual) {
                this.tryInitMainWorker(url, opts);
            }
        }, WORKER_REPORT_INTERVAL);
    }
    /**
     * 锁定主 worker 的创建
     */
    lockMainWorkerCreated() {
        this.mainWorkerCreatedLocked = true;
        setTimeout(() => {
            this.mainWorkerCreatedLocked = false;
        }, 1000);
    }
    /**
     * 降级到虚拟 worker
     */
    degradeToVirtualWorker() {
        if (this.workerType === WorkerType.Main) {
            if (this.mainWorkerReportTimer) {
                clearInterval(this.mainWorkerReportTimer);
                this.mainWorkerReportTimer = undefined;
            }
            this.terminate();
            SharedWorkerPolyfill.workers.delete(this.group);
            this.initVirtualWorker(this.url, this.opts);
        }
    }
    /**
     * 开启主 worker
     * @param url
     * @param opts
     */
    initMainWorker(url, opts) {
        if (this.mainWorkerCreatedLocked) {
            return;
        }
        if (getStorageAvaiable()) {
            if (this.virtualWorkerReportTimer) {
                clearInterval(this.virtualWorkerReportTimer);
                this.virtualWorkerReportTimer = undefined;
            }
            localStorage.setItem(this.mainWorkerCacheKey, this.workerId);
            localStorage.setItem(this.mainWorkerReportTimeCacheKey, Date.now() + '');
            if (this.mainWorkerReportTimer) {
                clearTimeout(this.mainWorkerReportTimer);
            }
            this.mainWorkerReportTimer = setInterval(() => {
                localStorage.setItem(this.mainWorkerReportTimeCacheKey, Date.now() + '');
            }, WORKER_REPORT_INTERVAL);
        }
        this.workerType = WorkerType.Main;
        this.mainWorkerCreatedAt = microSeconds();
        this.debugInfo(`init main worker ${this.mainWorkerCreatedAt}`);
        this.inited = true;
        this.virtualWorkerIds = [];
        const workerCache = SharedWorkerPolyfill.workers.get(this.group);
        if (workerCache) {
            this.ActualWorker = workerCache.ActualWorker;
        }
        else {
            this.ActualWorker = new Worker(url, opts);
            SharedWorkerPolyfill.workers.set(this.group, this);
        }
        this.sendMessageToVirtualWorker(EVENT_MAIN_WORKER_CREATED, {
            workerId: this.workerId,
            time: this.mainWorkerCreatedAt
        });
        this.afterInit();
    }
    /**
     * 尝试初始化主 worker 会先进行通信检测，避免多个页面同时初始化
     * @param url
     * @param opts
     * @param timeout
     * @returns
     */
    tryInitMainWorker(url, opts) {
        return new Promise((resolve) => {
            this.latestMainWorkerId = this.mainWorkerId;
            if (getStorageAvaiable() && !this.latestMainWorkerId) {
                this.debugInfo('no main worker cache, create immediately');
                this.initMainWorker(url, opts);
                return resolve(true);
            }
            const singleflight = Singleflight.getInstance();
            singleflight
                .do(`add_virtual_worker_${this.group}`, () => {
                return this.sendMessageToMainWorker(EVENT_ADD_VIRTUAL_WORKER, {});
            })
                .then(() => {
                this.debugInfo('main worker exists');
                resolve(false);
            }, () => {
                /**
                 * 如果不能通信，随机延迟一段时间后将当前 worker 升级为主 worker
                 */
                const timeout = Math.floor(Math.random() * 100);
                setTimeout(() => {
                    if (this.closed) {
                        return;
                    }
                    /**
                     * 如果其他页面已经升级为主 worker 了，跳过
                     */
                    if (this.latestMainWorkerId === this.mainWorkerId) {
                        this.debugInfo('main worker not exists, create');
                        this.initMainWorker(url, opts);
                        resolve(true);
                    }
                    else {
                        this.latestMainWorkerId = this.mainWorkerId;
                        this.debugInfo('main worker exists');
                        resolve(false);
                    }
                }, timeout);
            });
        });
    }
    /**
     * 开启虚拟 worker，所有的交互都是和主 worker 通信
     * @param url
     * @param opts
     * @param detectMainWorker 是否检测主 worker
     */
    initVirtualWorker(url, opts, detectMainWorker = false) {
        this.workerType = WorkerType.Virtual;
        this.latestMainWorkerId = this.mainWorkerId;
        if (detectMainWorker) {
            this.debugInfo('detect main worker first');
            this.tryInitMainWorker(url, opts).then((exists) => {
                if (!exists) {
                    this.inited = true;
                    this.afterVirtualWorkerInit();
                }
            });
        }
        else {
            this.debugInfo('create virtual worker');
            this.inited = true;
            this.afterVirtualWorkerInit();
        }
    }
    afterVirtualWorkerInit() {
        if (getStorageAvaiable()) {
            localStorage.setItem(this.virtualWorkerReportTimeCacheKey, Date.now() + '');
            this.virtualWorkerReportTimer = setInterval(() => {
                localStorage.setItem(this.virtualWorkerReportTimeCacheKey, Date.now() + '');
            }, WORKER_REPORT_INTERVAL);
        }
        this.afterInit();
    }
    afterInit() {
        for (const message of this.messageQueue) {
            this.innerPostMessage(message.message, message.options);
        }
        this.messageQueue = [];
        // 初始化事件监听
        for (const type of this.eventListenerMap.keys()) {
            if (this.workerType === WorkerType.Main) {
                this.addEventListenerToMainWorker(type);
            }
            else {
                this.sendMessageToMainWorker(EVENT_ADD_EVENT_LISTENER, {
                    type
                }).catch(() => { });
            }
        }
    }
    /**
     * 处理确认消息
     * @param callbackId
     * @param data
     */
    handleMessageAct(callbackId, data = null) {
        const callback = this.callbackMaps.get(callbackId);
        if (callback) {
            this.callbackMaps.delete(callbackId);
            const timer = this.callbackTimers.get(callbackId);
            if (timer) {
                clearTimeout(timer);
            }
            this.callbackTimers.delete(callbackId);
            if (!data) {
                callback.reject(new Error('no response'));
            }
            else {
                callback.resolve(data);
            }
        }
    }
    /**
     * 给主 worker 发送消息
     * @param event
     * @param data
     * @returns
     */
    sendMessageToMainWorker(event, data = {}) {
        return new Promise((resolve, reject) => {
            // 优先检测当前页面相同 group 的主 worker
            const mainWorker = SharedWorkerPolyfill.workers.get(this.group);
            if (mainWorker) {
                switch (event) {
                    case EVENT_POST_MESSAGE:
                        mainWorker.postMessageToMainWorker(data.message);
                        break;
                    case EVENT_ADD_EVENT_LISTENER:
                        mainWorker.addEventListenerToMainWorker(data.type);
                        break;
                }
                // 直接响应确认消息
                setTimeout(() => {
                    resolve({});
                });
            }
            else {
                const callbackId = 'cb_' + this.uniqueId++ + '_' + new Date().getTime();
                this.callbackMaps.set(callbackId, {
                    resolve,
                    reject
                });
                dispatchAppEvent(this.group, EVENT_TO_MAIN_WORKER, {
                    event,
                    data,
                    workerId: this.workerId,
                    callbackId
                });
                this.setSendMessageTimeout(callbackId);
            }
        });
    }
    /**
     * 发送消息超时
     * 延迟和容错率的折中值，过低错误率很高，过高延迟又大
     * @param callbackId
     */
    setSendMessageTimeout(callbackId) {
        const timeout = this.pageVisible ? 1000 : 3000;
        const sendTime = Date.now();
        const timer = setTimeout(() => {
            const needRetry = sendTime < this.pageVisibleTime;
            if (needRetry) {
                this.debugInfo('retry to check send message timeout');
                /**
                 * 消息是不会丢失的，只需要延迟检测一次即可
                 */
                this.setSendMessageTimeout(callbackId);
            }
            else {
                this.handleMessageAct(callbackId);
                this.debugInfo('send msg to main worker timeout');
            }
        }, timeout);
        this.callbackTimers.set(callbackId, timer);
    }
    /**
     * 从主 worker 收到消息
     * @param detail
     */
    onReceiveMessageFromMainWorker(detail) {
        const event = detail.event;
        if (event.startsWith(EVENT_ACT_PREFIX)) {
            // 确认消息
            this.handleMessageAct(detail.callbackId, detail.data);
        }
        else {
            // 其他消息
            switch (event) {
                case EVENT_MAIN_WORKER_CREATED:
                    this.lockMainWorkerCreated();
                    this.sendMessageToMainWorker(EVENT_ADD_VIRTUAL_WORKER).catch(() => { });
                    break;
                case EVENT_ADD_EVENT_LISTENER:
                    {
                        const data = detail.data;
                        let evt = null;
                        switch (data.type) {
                            case 'message':
                                evt = new MessageEvent(data.type, {
                                    data: data.data
                                });
                                break;
                            case 'messageerror':
                                evt = new MessageEvent(data.type, {
                                    data: data.data
                                });
                                break;
                            case 'error':
                                evt = new ErrorEvent(data.type, {
                                    message: data.message
                                });
                                break;
                        }
                        evt && EventBus.emit(this.group + data.type, evt);
                    }
                    break;
                case EVENT_TOGGLE_MAIN_WORKER:
                    {
                        const data = detail.data;
                        this.mainWorkerCreatedLocked = false;
                        this.initMainWorker(this.url, this.opts);
                    }
                    break;
            }
        }
    }
    /**
     * 给虚拟 worker 发送消息
     * @param data
     */
    sendMessageToVirtualWorker(event, data = {}, callbackId = '', workerId = '') {
        dispatchAppEvent(this.group, EVENT_TO_VIRTUAL_WORKER, {
            event,
            data,
            workerId,
            callbackId
        });
    }
    /**
     * 从虚拟 worker 收到消息
     * @param detail
     */
    onReceiveMessageFromVirtualWorker(detail) {
        // 发送 act 消息，通知虚拟 worker 收到消息
        if (getStorageAvaiable()) {
            localStorage.removeItem(detail.callbackId);
        }
        this.sendMessageToVirtualWorker(`${EVENT_ACT_PREFIX}${detail.event}`, {}, detail.callbackId, detail.workerId);
        switch (detail.event) {
            case EVENT_ADD_VIRTUAL_WORKER:
                {
                    const workerId = detail.workerId;
                    if (!this.virtualWorkerIds.includes(workerId)) {
                        this.virtualWorkerIds.push(workerId);
                    }
                }
                break;
            case EVENT_REMOVE_VIRTUAL_WORKER:
                {
                    const workerId = detail.workerId;
                    const index = this.virtualWorkerIds.indexOf(workerId);
                    if (index >= 0) {
                        this.virtualWorkerIds.splice(index, 1);
                    }
                }
                break;
            case EVENT_POST_MESSAGE:
                this.postMessageToMainWorker(detail.data.message);
                break;
            case EVENT_ADD_EVENT_LISTENER:
                this.addEventListenerToMainWorker(detail.data.type);
                break;
        }
    }
    /**
     * 绑定 onmessage、onmessageerror、onerror 回调
     * @param type
     * @param handler
     */
    bindEventListener(type) {
        this.eventListenerMap.set(type, true);
        if (this.inited) {
            if (this.workerType === WorkerType.Main) {
                this.addEventListenerToMainWorker(type);
            }
            else {
                this.sendMessageToMainWorker(EVENT_ADD_EVENT_LISTENER, {
                    type
                }).catch(() => { });
            }
        }
    }
    /**
     * An EventListener called when MessageEvent of type message is fired on the port—that is, when the port receives a message.
     */
    get onmessage() {
        var _a;
        if (this.useNativeSharedWorker) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessage;
        }
        else {
            return this.onMessageHandler;
        }
    }
    set onmessage(value) {
        if (this.useNativeSharedWorker) {
            ;
            this.ActualWorker.port.onmessage = value;
        }
        else {
            this.onMessageHandler = value;
            if (!this.eventOnMap.get('message')) {
                this.eventOnMap.set('message', true);
                EventBus.on(this.group + 'message', this, (_event, evt) => {
                    if (!this.closed) {
                        this.onMessageHandler && this.onMessageHandler(evt);
                    }
                });
            }
            this.bindEventListener('message');
        }
    }
    /**
     * An EventListener called when a MessageEvent of type MessageError is fired—that is, when it receives a message that cannot be deserialized.
     */
    get onmessageerror() {
        var _a;
        if (this.useNativeSharedWorker) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessageerror;
        }
        else {
            return this.onMessageErrorHandler;
        }
    }
    set onmessageerror(value) {
        if (this.useNativeSharedWorker) {
            ;
            this.ActualWorker.port.onmessageerror = value;
        }
        else {
            this.onMessageErrorHandler = value;
            if (!this.eventOnMap.get('messageerror')) {
                this.eventOnMap.set('messageerror', true);
                EventBus.on(this.group + 'messageerror', this, (_event, evt) => {
                    if (!this.closed) {
                        this.onMessageErrorHandler && this.onMessageErrorHandler(evt);
                    }
                });
            }
            this.bindEventListener('messageerror');
        }
    }
    /**
     * Starts the sending of messages queued on the port (only needed when using EventTarget.addEventListener; it is implied when using MessagePort.onmessage.)
     */
    start() {
        var _a;
        if (this.useNativeSharedWorker) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.start();
        }
    }
    postMessageToMainWorker(message) {
        ;
        this.ActualWorker.postMessage(message);
    }
    innerPostMessage(message, options) {
        var _a;
        if (options && options.heartCheck) {
            const timeout = typeof options.heartCheck === 'number' ? options.heartCheck : 2000;
            if (timeout > 0) {
                if (this.heartCheckTimer) {
                    clearTimeout(this.heartCheckTimer);
                }
                this.heartCheckTimer = setTimeout(() => {
                    if (!this.closed && this.pageVisible) {
                        this.restart();
                    }
                }, timeout);
            }
        }
        if (this.useNativeSharedWorker) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.postMessage(message);
        }
        else {
            if (this.workerType === WorkerType.Main) {
                this.postMessageToMainWorker(message);
            }
            else {
                this.sendMessageToMainWorker(EVENT_POST_MESSAGE, {
                    message
                }).catch(() => { });
            }
        }
    }
    /**
     * Clones message and transmits it to worker's global environment. transfer can be passed as a list of objects that are to be transferred rather than cloned.
     */
    postMessage(message, options) {
        if (!this.inited) {
            this.messageQueue.push({
                message,
                options
            });
        }
        else {
            this.innerPostMessage(message, options);
        }
    }
    /**
     * Immediately terminates the worker. This does not let worker finish its operations; it is halted at once. ServiceWorker instances do not support this method.
     */
    terminate() {
        if (this.workerType === WorkerType.Main && this.ActualWorker) {
            ;
            this.ActualWorker.terminate();
            this.ActualWorker = undefined;
        }
    }
    /**
     * Disconnects the port, so it is no longer active.
     */
    close() {
        var _a;
        if (this.useNativeSharedWorker) {
            this.closed = true;
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.close();
        }
        else {
            this.closed = true;
            return;
        }
    }
    /**
     * Returns a MessagePort object used to communicate with and control the shared worker.
     */
    get port() {
        return (this.useNativeSharedWorker ? this.ActualWorker.port : this.ActualWorker);
    }
    /**
     * Is an EventListener that is called whenever an ErrorEvent of type error event occurs.
     */
    get onerror() {
        return this.ActualWorker.onerror;
    }
    set onerror(value) {
        if (this.useNativeSharedWorker) {
            this.ActualWorker.onerror = value;
        }
        else {
            this.onErrorHandler = value;
            if (!this.eventOnMap.get('error')) {
                this.eventOnMap.set('error', true);
                EventBus.on(this.group + 'error', this, (_event, evt) => {
                    if (!this.closed) {
                        this.onErrorHandler && this.onErrorHandler(evt);
                    }
                });
            }
            this.bindEventListener('error');
        }
    }
    get onrestart() {
        return this.onRestartHandler;
    }
    set onrestart(value) {
        this.onRestartHandler = value;
    }
    addEventListenerToMainWorker(type) {
        const worker = this.ActualWorker;
        switch (type) {
            case 'message':
                worker.onmessage = (evt) => {
                    EventBus.emit(this.group + type, evt);
                    this.sendMessageToVirtualWorker(EVENT_ADD_EVENT_LISTENER, {
                        type,
                        data: evt.data
                    });
                };
                break;
            case 'messageerror':
                worker.onmessageerror = (evt) => {
                    EventBus.emit(this.group + type, evt);
                    this.sendMessageToVirtualWorker(EVENT_ADD_EVENT_LISTENER, {
                        type,
                        data: evt.data
                    });
                };
                break;
            case 'error':
                worker.onerror = (evt) => {
                    EventBus.emit(this.group + type, evt);
                    this.sendMessageToVirtualWorker(EVENT_ADD_EVENT_LISTENER, {
                        type,
                        data: {
                            message: evt.message
                        }
                    });
                };
                break;
        }
    }
    addEventListener(type, listener, options) {
        var _a;
        const isWorkerEventType = type === 'message' || type === 'messageerror' || type === 'error';
        if (this.useNativeSharedWorker) {
            if (isWorkerEventType) {
                const queue = this.eventListenerQueue.get(type) || [];
                queue.push(listener);
                this.eventListenerQueue.set(type, queue);
            }
            if (type === 'message' || type === 'messageerror') {
                return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.addEventListener(type, listener, options);
            }
            return this.ActualWorker.addEventListener(type, listener, options);
        }
        else {
            if (isWorkerEventType) {
                EventBus.on(this.group + type, listener, (_event, evt) => {
                    if (!this.closed) {
                        if (typeof listener === 'function') {
                            listener.call(this, evt);
                        }
                    }
                });
                this.bindEventListener(type);
            }
            else {
                EventBus.on(`customEvent_${this.uniqueId}_type`, listener, (_event, evt) => {
                    if (typeof listener === 'function') {
                        listener.call(this, evt);
                    }
                });
            }
        }
    }
    removeEventListener(type, listener, options) {
        var _a;
        const isWorkerEventType = type === 'message' || type === 'messageerror' || type === 'error';
        if (this.useNativeSharedWorker) {
            if (isWorkerEventType) {
                const queue = this.eventListenerQueue.get(type) || [];
                const index = queue.findIndex((item) => item === listener);
                if (index >= 0) {
                    queue.splice(index, 1);
                    this.eventListenerQueue.set(type, queue);
                }
            }
            if (type === 'message' || type === 'messageerror') {
                return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.removeEventListener(type, listener, options);
            }
            return this.ActualWorker.removeEventListener(type, listener, options);
        }
        else {
            if (isWorkerEventType) {
                EventBus.remove(this.group + type, listener);
            }
            else {
                EventBus.remove(`customEvent_${this.uniqueId}_type`, listener);
            }
        }
    }
    /**
     * Dispatches an event to this EventTarget. only support transfer event.type
     */
    dispatchEvent(event) {
        if (this.useNativeSharedWorker) {
            return this.ActualWorker.dispatchEvent(event);
        }
        else {
            EventBus.emit(`customEvent_${this.uniqueId}_type`, event);
            return true;
        }
    }
    /**
     * 重新启动
     */
    restart() {
        if (this.restartLock) {
            return;
        }
        if (this.useNativeSharedWorker) {
            /**
             * 原生 shared worker 在页面存在时只能修改 worker url 重启（追加随机数），当新开页面时还需要使用临时的 worker url，否则假死问题依旧无法解决
             * 最简单的方式就是降级到 Simulate
             */
            if (getStorageAvaiable()) {
                localStorage.setItem(DEGRADE_SIMULATE_WORKER_CACHE_KEY, '1');
            }
        }
        /**
         * 当前页面先重启
         */
        EventBus.emit(this.group + EVENT_RESTART);
        /**
         * 延迟重启其他页面，避免并发问题
         */
        setTimeout(() => {
            dispatchAppEvent(this.group, EVENT_RESTART);
        }, 1000);
    }
    /**
     * 重新启动的内部实现
     */
    innerRestart(url, opts) {
        var _a;
        if (this.restartLock) {
            return;
        }
        this.debugInfo(`worker restart`);
        this.restartLock = true;
        setTimeout(() => {
            if (this.onRestartHandler && !this.closed) {
                this.onRestartHandler();
            }
            this.restartLock = false;
        }, 3000);
        // 重启时降级为虚拟 worker
        if (this.useNativeSharedWorker) {
            const onMessage = this.onmessage;
            const onMessageError = this.onmessageerror;
            const onError = this.onerror;
            (_a = this.ActualWorker.port) === null || _a === void 0 ? void 0 : _a.close();
            this.degradeSimulateWorker = true;
            this.inited = false;
            this.initWorker(url, opts);
            if (onMessage) {
                this.onmessage = onMessage;
            }
            if (onMessageError) {
                this.onmessageerror = onMessageError;
            }
            if (onError) {
                this.onerror = onError;
            }
            for (const key of this.eventListenerQueue.keys()) {
                const listeners = this.eventListenerQueue.get(key);
                for (const listener of listeners) {
                    this.addEventListener(key, listener);
                }
            }
        }
        else {
            if (this.workerType === WorkerType.Main) {
                this.terminate();
                SharedWorkerPolyfill.workers.delete(this.group);
                this.initMainWorker(url, opts);
            }
        }
    }
    beforeUnload() {
        if (this.useNativeSharedWorker) {
            this.innerPostMessage({
                type: EVENT_UNLOAD
            });
        }
        else {
            if (this.workerType === WorkerType.Main) {
                // 关闭当前页面的 worker 并通知第一个虚拟 worker 切换为主 worker
                if (getStorageAvaiable()) {
                    if (this.mainWorkerId === this.workerId) {
                        localStorage.removeItem(this.mainWorkerCacheKey);
                    }
                }
                this.terminate();
                const firstVirtualWorkId = this.virtualWorkerIds[0];
                if (firstVirtualWorkId) {
                    this.sendMessageToVirtualWorker(EVENT_TOGGLE_MAIN_WORKER, {}, '', firstVirtualWorkId);
                }
            }
            else {
                // 通知主 worker 移除自己
                this.sendMessageToMainWorker(EVENT_REMOVE_VIRTUAL_WORKER).catch(() => { });
            }
        }
    }
}
SharedWorkerPolyfill.workers = new Map();
SharedWorkerPolyfill.listeners = new Map();
export default SharedWorkerPolyfill;
